import * as RadixDialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useIsPhone } from '@/ds/hooks/use-media'
import { DsIconButton } from '@/ds/button'
import { DsIcons } from '@/ds/icons'
import { useOverlayStore } from '@/ds/overlay-store'

export interface DsSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  footer?: ReactNode
  showClose?: boolean
  closeLabel?: string
  /** Phone: 95% instead of 90% height. Desktop: full-height drawer either way. */
  fullHeight?: boolean
  isDismissible?: boolean
  /**
   * Asked before Escape, a click outside or the X closes the sheet; it closes only on true. Off by default. Use it for a form
   * with unsaved input (`!dirty || DsOverlay.confirm(...)`). A `close()` from the contents is never asked.
   */
  confirmDismiss?: () => boolean | Promise<boolean>
  scrollable?: boolean
  // Default bottom on phones, right on desktop. `center` is a modal dialog for a short form on md and
  // up; phones still get the bottom sheet.
  side?: 'bottom' | 'right' | 'center'
  /** Drawer width on desktop: md 440 · lg 600 · xl 800. */
  size?: 'md' | 'lg' | 'xl'
  children: ReactNode
  className?: string
}

const drawerWidth = { md: 'w-[440px]', lg: 'w-[600px]', xl: 'w-[800px]' }

export function DsSheet({ open, onClose, title, description, footer, showClose = true, closeLabel = 'Close', fullHeight, isDismissible = true, confirmDismiss, scrollable = true, side, size = 'md', children, className }: DsSheetProps) {
  const phone = useIsPhone()
  const placement = side === 'center' ? (phone ? 'bottom' : 'center') : side ?? (phone ? 'bottom' : 'right')
  const bottom = placement === 'bottom'
  const dismiss = async () => {
    if (!isDismissible) return
    if (confirmDismiss && !(await confirmDismiss())) return
    onClose()
  }
  return (
    <RadixDialog.Root open={open} onOpenChange={(o) => !o && void dismiss()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim animate-ds-fade-in data-[state=closed]:animate-ds-fade-out" />
        <RadixDialog.Content
          // Focus the panel, not the close button: a focused icon button shows its tooltip, and the
          // first Escape would only close that tooltip instead of the sheet.
          onOpenAutoFocus={(e) => {
            if (!showClose) return
            e.preventDefault()
            ;(e.currentTarget as HTMLElement).focus()
          }}
          onEscapeKeyDown={(e) => !isDismissible && e.preventDefault()}
          onPointerDownOutside={(e) => !isDismissible && e.preventDefault()}
          className={cn(
            'fixed z-50 flex flex-col border bg-card text-card-foreground outline-none',
            bottom
              ? cn('inset-x-0 bottom-0 rounded-t-2xl animate-ds-slide-up data-[state=closed]:animate-ds-fade-out', fullHeight ? 'max-h-[95dvh]' : 'max-h-[90dvh]')
              : placement === 'center'
                ? cn('inset-0 m-auto h-fit max-h-[85dvh] max-w-[calc(100vw-32px)] rounded-2xl pb-2 shadow-xl animate-ds-scale-in data-[state=closed]:animate-ds-fade-out', drawerWidth[size])
                : cn('inset-y-0 right-0 max-w-full border-l animate-ds-slide-left data-[state=closed]:animate-ds-fade-out', drawerWidth[size]),
            className,
          )}
        >
          {bottom && <span aria-hidden className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border" />}
          {(title || showClose) && (
            <div className="flex items-start gap-3 px-gutter pb-2 pt-3 md:px-6">
              <div className="min-w-0 flex-1">
                {title ? <RadixDialog.Title className="font-display text-title2 text-foreground">{title}</RadixDialog.Title> : <RadixDialog.Title className="sr-only">Sheet</RadixDialog.Title>}
                {description && <RadixDialog.Description className="mt-0.5 text-small text-muted-foreground">{description}</RadixDialog.Description>}
              </div>
              {showClose && (
                <RadixDialog.Close asChild>
                  <DsIconButton icon={DsIcons.close} semanticLabel={closeLabel} size="sm" />
                </RadixDialog.Close>
              )}
            </div>
          )}
          <div className={cn('flex-1 px-gutter pb-4 md:px-6', scrollable && 'overflow-y-auto overscroll-contain')}>{children}</div>
          {footer && <div className="border-t bg-card px-gutter pb-[max(12px,env(safe-area-inset-bottom))] pt-3 md:px-6">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

// The same DsSheet, defaulting to the wider `lg` drawer; phones still get a bottom sheet.
export function DsDrawer({ size = 'lg', ...props }: Omit<DsSheetProps, 'side'>) {
  return <DsSheet {...props} size={size} />
}

export interface DsSheetShowOptions<T> extends Omit<DsSheetProps, 'open' | 'onClose' | 'children' | 'footer'> {
  /** Build the contents; call `close(result)` to dismiss with a value. */
  builder: (close: (result?: T) => void) => ReactNode
  footer?: ReactNode | ((close: (result?: T) => void) => ReactNode)
}

/** Imperative sheet: `const picked = await showSheet({ title, builder })`. Resolves undefined when dismissed. */
export function showSheet<T = unknown>({ builder, footer, ...props }: DsSheetShowOptions<T>): Promise<T | undefined> {
  return useOverlayStore.getState().push((close) => {
    const done = (r?: T) => close(r)
    return (
      <DsSheet open onClose={() => done(undefined)} footer={typeof footer === 'function' ? footer(done) : footer} {...props}>
        {builder(done)}
      </DsSheet>
    )
  }) as Promise<T | undefined>
}

export function showFullScreen<T = unknown>(render: (close: (result?: T) => void) => ReactNode): Promise<T | undefined> {
  return useOverlayStore.getState().push((close) => (
    <RadixDialog.Root open onOpenChange={() => undefined}>
      <RadixDialog.Portal>
        <RadixDialog.Content className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background outline-none animate-ds-scale-in data-[state=closed]:animate-ds-fade-out" onEscapeKeyDown={(e) => e.preventDefault()}>
          <RadixDialog.Title className="sr-only">Dialog</RadixDialog.Title>
          {render((r?: T) => close(r))}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )) as Promise<T | undefined>
}
